import { useState } from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Fade from "@mui/material/Fade";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import { makeStyles } from "@mui/styles";
import {
  SimpleForm,
  Toolbar,
  Confirm,
  useNotify,
  useRefresh,
  AutocompleteInput,
  required,
  ReferenceInput,
  useGetOne,
} from "react-admin";
import mbaas from "../../provider/Mbaas";
import TextField from "@mui/material/TextField";
import UpdateIcon from "@mui/icons-material/Update";
import Swal from "sweetalert2";
import CloseIcon from "@mui/icons-material/Close";
import { useTransferVirtualMsisdn } from "./hooks/useTransferVirtualMsisdn";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 700,
  bgcolor: "background.paper",
  borderRadius: "7px",
  boxShadow: 24,
  p: 4,
};

export default function SwitchVNModal({
  open,
  handleClose,
  props,
  currentRecord,
}: any) {
  const classes = useStyles();
  const notify = useNotify();
  const refresh = useRefresh();
  const { transferVirtualMsisdnAsync } = useTransferVirtualMsisdn();
  const [selectedSubscriberId, setSelectedSubscriberId] = useState<
    string | null
  >(null);

  // Fetch the full subscriber data when an ID is selected
  const { data: selectedSubscriberData, isLoading: isLoadingSubscriber } =
    useGetOne(
      "subscribers",
      { id: selectedSubscriberId },
      { enabled: !!selectedSubscriberId }
    );

  const handleSelectSubscriber = (subscriberId: string) => {
    setSelectedSubscriberId(subscriberId);
  };

  const CustomToolbar = ({ selectedSubscriber, ...props }: any) => {
    const useStyles = makeStyles(() => ({
      toolbar: {
        display: "flex",
        justifyContent: "space-between",
      },
    }));
    const [isLoadingOtp, setIsLoadingOtp] = useState(false);
    const [openOtpModal, setOpenOtpModal] = useState(false);

    const [otpCode, setOtpCode] = useState("");

    const handleClick = () => {
      setOpenOtpModal(true);
    };
    const handleDialogClose = () => {
      setOpenOtpModal(false);
    };

    const handleSwitchVN = async () => {
      setIsLoadingOtp(true);
      const { error: valTotpErr, data: valTotpData } =
        await mbaas.client.invokeApi("console-validate-totp", {
          token: otpCode,
        });
      if (valTotpErr) {
        Swal.fire({
          icon: "error",
          title: `Request failed: ${valTotpErr?.error}`,
          target: "#muiModal",
        });
      }
      if (valTotpData === "OK") {
        const virtualMsisdn =
          selectedSubscriberData?.virtual_msisdn_user_id_list?.data?.[0]
            ?.msisdn;

        if (!virtualMsisdn) {
          Swal.fire({
            icon: "error",
            title: `Cannot proceed`,
            text: `The selected subscriber does not have a Virtual Number (VN) assigned. Please choose a subscriber with a VN to proceed.`,
            target: "#muiModal",
          });
          setIsLoadingOtp(false);
          return;
        }

        try {
          const result = await transferVirtualMsisdnAsync({
            new_user_id: currentRecord?.id,
            old_user_id: selectedSubscriberId!,
            virtual_msisdn: virtualMsisdn,
          });

          if (result?.data?.transferVirtualMsisdn?.status === "OK") {
            handleClose();
            refresh();
            setOpenOtpModal(false);
          }
        } catch (error: any) {
          Swal.fire({
            icon: "error",
            title: `Transfer failed`,
            text: error?.message || "Failed to transfer virtual number",
            target: "#muiModal",
          });
          handleClose();
        }
      }
      setIsLoadingOtp(false);
    };

    return (
      <>
        <Toolbar {...props} classes={useStyles}>
          <Button
            variant="contained"
            onClick={handleClick}
            startIcon={<UpdateIcon />}
            // disabled={!reason || !coinBalance}
          >
            Proceed
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleClose}
            startIcon={<CloseIcon />}
            sx={{
              marginLeft: "10px",
            }}
          >
            Cancel
          </Button>
        </Toolbar>
        <Confirm
          id="muiModal"
          isOpen={openOtpModal}
          loading={otpCode.length < 6 ? true : isLoadingOtp}
          title="OTP Code"
          content={
            <>
              <Typography variant="caption" gutterBottom>
                <b>Important!</b> By confirming this dialog it will update
                subscriber VN,<br></br> are you sure want to proceed?
              </Typography>
              <TextField
                type="number"
                fullWidth
                data-test-id="input-otp"
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value)}
              />
            </>
          }
          onConfirm={() => handleSwitchVN()}
          onClose={() => handleDialogClose()}
        />
      </>
    );
  };

  return (
    <div>
      <Modal
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        open={open}
        onClose={handleClose}
        closeAfterTransition
      >
        <Fade in={open}>
          <Box sx={style}>
            <Typography variant="h6" gutterBottom component="div">
              Transfer Virtual Number (VN)
            </Typography>
            <SimpleForm
              toolbar={
                <CustomToolbar
                  props={props}
                  selectedSubscriber={selectedSubscriberData}
                />
              }
            >
              <div className={classes.root}>
                <div className={classes.form}>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <Typography
                        variant="subtitle1"
                        display="block"
                        gutterBottom
                      >
                        Find below details of subscriber.
                      </Typography>
                      <ReferenceInput
                        key="user_id"
                        source="user_id"
                        reference="subscribers"
                        label="Subscriber Name"
                      >
                        <AutocompleteInput
                          fullWidth
                          className={classes.input}
                          source="name"
                          data-testid="search-subscriber"
                          optionText={(choice) => {
                            return `Name: ${choice.name} (Phone: ${
                              choice.phone
                            } | VN: ${
                              choice?.virtual_msisdn_user_id_list?.data?.[0]
                                ?.msisdn ?? "N/A"
                            })`;
                          }}
                          isRequired={true}
                          validate={required()}
                          onChange={(value) => handleSelectSubscriber(value)}
                          label="Type in a name"
                        />
                      </ReferenceInput>
                    </Grid>
                  </Grid>
                </div>
              </div>
            </SimpleForm>
          </Box>
        </Fade>
      </Modal>
    </div>
  );
}

const useStyles = makeStyles(() => ({
  root: {
    display: "flex",
    alignItems: "flex-start",
    width: "100%",
  },
  form: {
    flexGrow: 1,
  },
  input: {
    marginBottom: "-20px ",
  },
  text: {
    color: "#0000008A",
  },
}));
